import React from "react";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { walletPromotionStore } from "../stores/walletPromotion";
import { configStore } from "../stores/config";
import { walletPromotionAddModalStore } from "../stores/walletPromotionAddModal";
import { walletPromotionEditModalStore } from "../stores/walletPromotionEditModal";

class WalletPromotion extends React.Component {
	componentDidMount() {
		this.props.Interface.loadWalletPromotionList();
	}

	safeJsonParse = (value, fallback = {}) => {
		if (!value || typeof value !== "string") return value || fallback;
		try {
			return JSON.parse(value);
		} catch {
			return fallback;
		}
	};

	formatDateTime = (value) => {
		if (!value) return "-";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	};

	formatDateRangeValue = (value) => {
		if (!value) return null;
		if (typeof value === "string") return value.replace(" ", "T");
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 19);
	};

	getBadgeTypeLabel = (value) => {
		if (value === "from") return "From";
		if (value === "to") return "To";
		return "No text";
	};

	getBadgeActionLabel = (value) => {
		if (value === "live_chat") return "Live chat";
		if (value === "link") return "Link";
		return "None";
	};

	normalizePlacement = (value) => {
		if (Array.isArray(value)) return value;
		if (typeof value === "string") {
			try {
				const parsed = JSON.parse(value);
				if (Array.isArray(parsed)) return parsed;
			} catch {}
			return value ? [value] : [];
		}
		return [];
	};

	openEditModal = (item) => {
		const badgeSettings = this.safeJsonParse(item.badgeSettings, {});
		const badgeTypeValue = badgeSettings.type || "none";
		const badgeActionValue = badgeSettings.action || "none";
		const placementValues = this.normalizePlacement(item.placement);
		const placementOptions = this.props.walletPromotionEditModal.currentOption.placementType.filter((option) => placementValues.includes(option.value));
		const activeOption = item.active ? { value: "1", label: "Enabled" } : { value: "0", label: "Disabled" };

		this.props.setWalletPromotionEditModal({
			...this.props.walletPromotionEditModal,
			show: true,
			id: item.id,
			placement: placementOptions,
			priority: String(item.priority ?? ""),
			startDate: this.formatDateRangeValue(item.startDate),
			endDate: this.formatDateRangeValue(item.endDate),
			active: activeOption,
			linkTranslationType: badgeSettings.linkTranslationType || "",
			link: badgeSettings.link || "",
			textLink: badgeSettings.textLink || "",
			badgePercent: badgeSettings.percent ?? "",
			badgeType: {
				value: badgeTypeValue,
				label: this.getBadgeTypeLabel(badgeTypeValue),
			},
			badgeAction: {
				value: badgeActionValue,
				label: this.getBadgeActionLabel(badgeActionValue),
			},
			cohortConditions: item.cohortConditions || "",
			conditions: [],
		});
	};

	render() {
		const { walletPromotion } = this.props;

		return (
			<>
				{walletPromotion.loaded && walletPromotion.error.text.length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p">{walletPromotion.error.text}</Box>
							<hr />
							<Box variant="p">
								request error code: {walletPromotion.error.code} | request timestamp: {walletPromotion.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}
				<Table
					stickyHeader
					trackBy="id"
					loading={!walletPromotion.loaded}
					sortingColumn={{ sortingField: walletPromotion.sorting.sortingColumn.column }}
					sortingDescending={walletPromotion.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setWalletPromotion({
							...walletPromotion,
							sorting: {
								...walletPromotion.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => this.props.Interface.loadWalletPromotionList(), 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => (
								<Button iconName="status-in-progress" variant="normal" onClick={() => this.openEditModal(item)}>
									{item.id}
								</Button>
							),
						},
						{
							id: "placement",
							header: "Placement",
							sortingField: "placement",
							cell: (item) => {
								const placements = this.normalizePlacement(item.placement);
								return placements.length ? placements.join(", ") : "-";
							},
						},
						{
							id: "badgeSettings",
							header: "Badge settings",
							sortingField: "badgeSettings",
							cell: (item) => {
								const badgeSettings = this.safeJsonParse(item.badgeSettings, {});
								const percent = badgeSettings.percent ?? "-";
								const type = this.getBadgeTypeLabel(badgeSettings.type);
								const action = this.getBadgeActionLabel(badgeSettings.action);
								return `${percent}% (${type}, ${action})`;
							},
						},
						{ id: "priority", header: "Priority", sortingField: "priority", cell: (item) => item.priority ?? "-" },
						{ id: "startDate", header: "Start at", sortingField: "startDate", cell: (item) => this.formatDateTime(item.startDate) },
						{ id: "endDate", header: "End at", sortingField: "endDate", cell: (item) => this.formatDateTime(item.endDate) },
						{
							id: "active",
							header: "Active",
							sortingField: "active",
							cell: (item) => (Number(item.active) === 1 ? "Enabled" : "Disabled"),
						},
						{ id: "createdAt", header: "Created at", sortingField: "createdAt", cell: (item) => this.formatDateTime(item.createdAt) },
					]}
					items={walletPromotion.data}
					pagination={
						<Pagination
							disabled={!walletPromotion.loaded}
							currentPageIndex={walletPromotion.pagination.current}
							pagesCount={walletPromotion.pagination.total}
							onChange={(page) => {
								this.props.setWalletPromotion({
									...walletPromotion,
									pagination: { ...walletPromotion.pagination, current: page.detail.currentPageIndex },
								});
								setTimeout(() => this.props.Interface.loadWalletPromotionList(), 250);
							}}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: this.props.config.walletPromotion.perPage }}
							pageSizePreference={{
								title: "Wallet promotions per page",
								options: this.props.config.walletPromotion.preferences.perPage,
							}}
							onConfirm={(preferences) => {
								localStorage.setItem("walletPromotion.perPage", preferences.detail.pageSize);
								this.props.setConfig({
									...this.props.config,
									walletPromotion: { ...this.props.config.walletPromotion, perPage: preferences.detail.pageSize },
								});
								setTimeout(() => this.props.Interface.loadWalletPromotionList(), 250);
							}}
						/>
					}
					header={
						<Header
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="primary"
										iconName="add-plus"
										disabled={!walletPromotion.loaded}
										onClick={() => this.props.setWalletPromotionAddModal({ ...this.props.walletPromotionAddModal, show: true })}
									>
										Add wallet promo
									</Button>
								</SpaceBetween>
							}
						>
							Wallet promotions ({walletPromotion.pagination.count})
						</Header>
					}
					empty="No wallet promotions found with requested filter"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const walletPromotion = useRecoilValue(walletPromotionStore);
		const setWalletPromotion = useSetRecoilState(walletPromotionStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const walletPromotionAddModal = useRecoilValue(walletPromotionAddModalStore);
		const setWalletPromotionAddModal = useSetRecoilState(walletPromotionAddModalStore);
		const walletPromotionEditModal = useRecoilValue(walletPromotionEditModalStore);
		const setWalletPromotionEditModal = useSetRecoilState(walletPromotionEditModalStore);

		return (
			<Component
				{...props}
				walletPromotion={walletPromotion}
				setWalletPromotion={setWalletPromotion}
				config={config}
				setConfig={setConfig}
				walletPromotionAddModal={walletPromotionAddModal}
				setWalletPromotionAddModal={setWalletPromotionAddModal}
				walletPromotionEditModal={walletPromotionEditModal}
				setWalletPromotionEditModal={setWalletPromotionEditModal}
			/>
		);
	};
}

export default withRecoil(WalletPromotion);